<template>
  <div class="location-popover-content">
    <div
      v-if="filterHistoryList.length > 0 && Y_WEB_SEARCH_HISTORY"
      class="history-wrap"
    >
      <div class="section mb-[16px] flex items-center justify-between">
        <div class="section-title">{{ t("shared.common.historySearch") }}</div>
        <font-icon
          class="search-icon"
          icon="icon-shanchu"
          @click.stop="handleDelete"
        />
      </div>
      <searchHistory :list="filterHistoryList" @select="handleSelectHistory" />
    </div>

    <!-- 如果有自定义组件，使用自定义组件（它内部包含了 placeholder 和 cityPanel） -->
    <component
      v-if="customLocationCityPanel"
      :is="customLocationCityPanel"
      @select="handleSelectCity"
    />

    <!-- 否则使用默认逻辑 -->
    <template
      v-else-if="!customLocationCityPanel && Y_WEB_SEARCH_LOCATION_POPOVER"
    >
      <div
        v-if="filterHistoryList.length > 0 && tabList.length > 0"
        class="placeholder"
      />

      <div v-if="tabList.length > 0">
        <cityPanel
          :tabList="tabList"
          :showGroupName="false"
          @select="handleSelectCity"
        />
      </div>
    </template>

    <div v-if="isLoading" class="loading-state">
      <el-skeleton :rows="5" animated />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ElMessageBox } from "element-plus";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, shallowRef } from "vue";
import { useI18n } from "vue-i18n";

import { getCityList } from "@/api/rest/common";
import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useToggles } from "@/hooks/useToggles";

import cityPanel from "./components/cityPanel.vue";
import searchHistory from "./components/searchHistory.vue";
import { ICity } from "./types";
const { t, locale } = useI18n();

const emit = defineEmits(["select"]);

const YHotelStore = useHotelStoreHook();
const { historyList } = storeToRefs(YHotelStore);
const yChannelStore = useYChannelStoreHook();

const originCityList = ref<ICity[]>([]);
const isLoading = ref(true);
const Y_WEB_SEARCH_LOCATION_POPOVER = useToggles(
  "Y_WEB_SEARCH_LOCATION_POPOVER"
);
const Y_WEB_SEARCH_HISTORY = useToggles("Y_WEB_SEARCH_HISTORY");
// 动态加载自定义 LocationCityPanel 组件
const customLocationCityPanel = shallowRef(null);

const hotCityList = computed(() => {
  return originCityList.value.map(mapCityByLang);
});
const filterHistoryList = computed(() => {
  return historyList.value.filter(item => {
    const collectionName =
      item?.info?.collectionName || item?.target?.collectionName || "";
    return collectionName !== "googleMapPlace";
  });
});
const tabList = computed(() => {
  const list = hotCityList.value;

  return [
    {
      name: t("shared.common.hotDomesticCityTitle"),
      groups: [
        {
          label: t("shared.common.hotCity"),
          list: filterCityList(list, ["中国", "中国台湾"], true)
        }
      ]
    },
    {
      name: t("shared.common.hotInternationalCityTitle"),
      groups: [
        {
          label: t("shared.common.hotCity"),
          list: filterCityList(list, ["中国", "中国台湾"], false)
        }
      ]
    }
  ];
});

const filterCityList = (
  list: ICity[],
  filterCountry: string[],
  includes: Boolean = true
) => {
  return list.filter(item =>
    includes
      ? filterCountry.includes(item.country)
      : !filterCountry.includes(item.country)
  );
};

const handleDelete = () => {
  ElMessageBox.confirm(t("shared.common.deleteHistory")).then(() => {
    YHotelStore.deleteHistory();
  });
};
const handleSelect = item => {
  emit("select", item);
};

const handleSelectCity = item => {
  handleSelect({
    type: "city",
    data: item
  });
};

const handleSelectHistory = item => {
  handleSelect({
    type: "history",
    data: item
  });
};

const mapCityByLang = (city: ICity) => {
  const lang = locale.value;
  if (lang === "zh") {
    return {
      ...city,
      name: city.city,
      countryName: city.country,
      provinceName: city.province
    };
  }

  const t = city.translation?.[lang] || city.translation?.en;
  return {
    ...city,
    name: t?.name || city.city,
    countryName: t?.country || city.country,
    provinceName: t?.province || city.province
  };
};

// 加载自定义 LocationCityPanel 组件
const loadCustomLocationCityPanel = () => {
  const customComponent =
    yChannelStore.configs?.components?.LocationCityPanel?.component;
  if (customComponent) {
    customLocationCityPanel.value = customComponent;
  }
};

onMounted(async () => {
  isLoading.value = true;
  // 加载自定义组件
  loadCustomLocationCityPanel();
  try {
    const [_, cityData] = await Promise.all([
      YHotelStore.getSearchHistory(),
      getCityList({ hot: true })
    ]);
    originCityList.value = cityData;
  } catch (error) {
    console.error("Failed to load discovery data:", error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style lang="scss" scoped>
.location-popover-content {
  .search-icon {
    font-size: 16px;
    cursor: pointer;
  }
  .section-title {
    font-size: 16px;
    color: #000b1e;
    font-family: var(--y-regular-font);
  }
  .placeholder {
    width: 100%;
    height: 10px;
    background: #fafafa;
  }
  .history-wrap {
    padding: 24px 24px 20px;
  }
}
</style>
